import type { Err, Ids } from './common';
import type { Bundle } from './types';

/*
 * Port of shared-cs `ContentValidator.Tech.cs` (`TechSections`: techTiers, crafting, fieldKit and the recipe, item and module v2
 * fields), S2-TEC. Same errors, same wording, same order (the C# walks its id dictionaries in insertion order: first entry per id).
 * Absent fields are their C# defaults; explicit nulls are treated like the C# `?? new` (no "missing" errors in this partial).
 */

type Req = {
  facility?: string | null;
  level?: number | null;
  tool?: string | null;
  blueprint?: number | null;
} | null;
type Stack = { itemId?: string | null; quantity?: number | null } | null;
type Power = { outputW?: number | null; storageWh?: number | null; solarWhPerHour?: number | null };
type Level = {
  level?: number | null;
  tier?: number | null;
  requires?: Req[] | null;
  unlocksTier?: number | null;
  power?: Power | null;
  powerWhPerDay?: number | null;
  roomSlot?: string | null;
} | null;
type Module = { id?: string | null; levels?: Level[] | null };
type Recipe = {
  id?: string | null;
  inputs?: Stack[] | null;
  outputs?: Stack[] | null;
  requiredModuleId?: string | null;
  requiredModuleLevel?: number | null;
  tier?: number | null;
  requires?: Req[] | null;
  powerWh?: number | null;
  craftSeconds?: number | null;
  where?: string | null;
};
type Item = { id?: string | null; category?: string | null; tier?: number | null };
type Tier = {
  tier?: number | null;
  gateModuleId?: string | null;
  keyParts?: string[] | null;
  milestones?: string[] | null;
  questMissionId?: string | null;
  rebuild?: string[] | null;
};

const RAW = 'raw';
const MATERIAL = 'material';
const INTERMEDIATE = 'intermediate';
const PART = 'part';
const WORKSHOP_TOOL = 'workshop_tool';
const KEY_PART = 'key_part';
const isField = (where: string | null | undefined) => where === 'field';
const n0 = (v: number | null | undefined) => v ?? 0;
const s0 = (v: string | null | undefined) => v ?? '';
/** C# `int.TryParse` (NumberStyles.Integer): optional surrounding whitespace and sign, Int32 range. */
function tryInt(s: string): number | undefined {
  if (!/^\s*[+-]?\d+\s*$/.test(s)) return undefined;
  const v = Number(s.trim());
  return v >= -2147483648 && v <= 2147483647 ? v : undefined;
}

export function validateTech(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  const items = new Map<string, Item>();
  for (const i of (b.items ?? []) as (Item | null)[])
    if (i != null && i.id && !items.has(i.id)) items.set(i.id, i);
  const modules = new Map<string, Module>();
  const moduleIndex = new Map<string, number>();
  ((b.baseModules ?? []) as (Module | null)[]).forEach((m, n) => {
    if (m != null && m.id && !modules.has(m.id)) {
      modules.set(m.id, m);
      moduleIndex.set(m.id, n);
    }
  });
  const recipes: [Recipe, number][] = [];
  ((b.recipes ?? []) as (Recipe | null)[]).forEach((r, n) => {
    if (r != null) recipes.push([r, n]);
  });

  const tiers = new Map<number, Tier>();
  const tierIndex = new Map<number, number>();
  ((b.techTiers ?? []) as (Tier | null)[]).forEach((t, n) => {
    if (t == null) return;
    const tier = n0(t.tier);
    if (tiers.has(tier)) err('techTiers', n, 'tier', `Tech tier ${tier} is listed twice.`);
    else {
      tiers.set(tier, t);
      tierIndex.set(tier, n);
    }
  });

  const lootItems = new Set<string>();
  for (const t of b.lootTables ?? [])
    for (const e of t?.entries ?? [])
      if (e != null && e.itemId && (e.weight ?? 0) > 0) lootItems.add(e.itemId);

  const keyPartDefs = new Map<string, { tier?: number | null }>();
  for (const k of b.keyParts ?? [])
    if (k != null && k.id && !keyPartDefs.has(k.id)) keyPartDefs.set(k.id, k);

  const isCat = (id: string | null | undefined, cat: string) =>
    items.get(id ?? '')?.category === cat;
  const isRaw = (id: string) => isCat(id, RAW) || isCat(id, MATERIAL);
  const isRebuild = (r: Recipe) => (r.requires ?? []).some((q) => q != null && n0(q.blueprint) > 0);
  const levelsOf = (m: Module) => m.levels ?? [];
  const recipeErr = (n: number, path: string, m: string) => err('recipes', n, path, m);
  const moduleErr = (id: string, path: string, m: string) =>
    err('baseModules', moduleIndex.get(id), path, m);

  // ---- requires references and the v2 fields
  function checkRequires(owner: string, reqs: Req[] | null | undefined, at: (m: string) => void) {
    for (const q of reqs ?? []) {
      if (q == null) {
        at(`${owner} has an empty requirement.`);
        continue;
      }
      const set = (q.facility ? 1 : 0) + (q.tool ? 1 : 0) + (n0(q.blueprint) > 0 ? 1 : 0);
      if (set !== 1) {
        at(
          `${owner} has a requirement that names ${set === 0 ? 'nothing' : 'more than one thing'} (set one of facility, tool or blueprint).`,
        );
        continue;
      }
      if (q.facility) {
        const fm = modules.get(q.facility);
        const count = fm ? levelsOf(fm).length : 0;
        if (!fm) at(`${owner} needs unknown facility '${q.facility}'.`);
        else if (n0(q.level) < 0 || Math.max(1, n0(q.level)) > count)
          at(
            `${owner} needs level ${n0(q.level)} of facility '${q.facility}', which has levels 1..${count}.`,
          );
      } else if (q.tool) {
        if (!items.has(q.tool)) at(`${owner} needs unknown tool '${q.tool}'.`);
        else if (!isCat(q.tool, WORKSHOP_TOOL))
          at(`${owner} needs '${q.tool}' as a tool, but it isn't a workshop_tool.`);
      } else if (tiers.size > 0 && !tiers.has(n0(q.blueprint))) {
        at(`${owner} needs the blueprint of tier ${n0(q.blueprint)}, which doesn't exist.`);
      }
    }
  }

  for (const [r, n] of recipes) {
    const owner = `Recipe '${s0(r.id)}'`;
    checkRequires(owner, r.requires, (m) => recipeErr(n, 'requires', m));
    if (n0(r.tier) < 0) recipeErr(n, 'tier', `${owner} has a negative tier.`);
    if (n0(r.powerWh) < 0) recipeErr(n, 'powerWh', `${owner} has negative powerWh.`);
    if (n0(r.craftSeconds) < 0) recipeErr(n, 'craftSeconds', `${owner} has negative craftSeconds.`);
    if (r.where && r.where !== 'home' && r.where !== 'field')
      recipeErr(n, 'where', `${owner} has where '${r.where}' (use home or field).`);
  }

  for (const [id, m] of modules)
    levelsOf(m).forEach((l, li) => {
      if (l == null) return;
      const owner = `Facility '${id}' level ${n0(l.level)}`;
      const at = (p: string) => (msg: string) => moduleErr(id, `levels.${li}${p}`, msg);
      checkRequires(owner, l.requires, at('.requires'));
      if (n0(l.tier) < 0) at('.tier')(`${owner} has a negative tier.`);
      if (n0(l.powerWhPerDay) < 0) at('.powerWhPerDay')(`${owner} has a negative powerWhPerDay.`);
      if (
        l.power != null &&
        (n0(l.power.outputW) < 0 || n0(l.power.storageWh) < 0 || n0(l.power.solarWhPerHour) < 0)
      )
        at('.power')(`${owner} has negative power numbers.`);
      if (n0(l.unlocksTier) < 0) at('.unlocksTier')(`${owner} has a negative unlocksTier.`);
      if (n0(l.unlocksTier) > 0 && n0(l.level) !== 1)
        at('.unlocksTier')(`${owner} unlocks a tier, but only a first level can.`);
      if (n0(l.unlocksTier) > 0 && tiers.size > 0 && !tiers.has(n0(l.unlocksTier)))
        at('.unlocksTier')(`${owner} unlocks tier ${n0(l.unlocksTier)}, which doesn't exist.`);
    });

  ((b.items ?? []) as (Item | null)[]).forEach((i, n) => {
    if (i == null || !i.id || items.get(i.id) !== i) return;
    if (n0(i.tier) < 0) err('items', n, 'tier', `Item '${i.id}' has a negative tier.`);
  });

  // ---- field recipes and the field kit
  for (const [r, n] of recipes) {
    if (!isField(r.where)) continue;
    const owner = `Field recipe '${s0(r.id)}'`;
    if ((r.requires != null && r.requires.length > 0) || r.requiredModuleId)
      recipeErr(
        n,
        'requires',
        `${owner} has requirements (the field kit needs no tools, stations or tier).`,
      );
    if (n0(r.powerWh) > 0) recipeErr(n, 'powerWh', `${owner} needs power.`);
    for (const s of r.inputs ?? []) {
      if (s == null || !items.has(s.itemId ?? '')) continue;
      const id = s.itemId ?? '';
      if (!isRaw(id) && !lootItems.has(id))
        recipeErr(
          n,
          'inputs',
          `${owner} uses '${id}', which is neither raw nor found in a loot table.`,
        );
    }
    for (const s of r.outputs ?? []) {
      if (s == null) continue;
      if (isCat(s.itemId, KEY_PART) || isCat(s.itemId, WORKSHOP_TOOL))
        recipeErr(
          n,
          'outputs',
          `${owner} makes '${s0(s.itemId)}' (never a key part or a workshop tool).`,
        );
    }
  }

  const kit = b.fieldKit ?? {};
  const maxTimes = kit.maxTimes ?? 5;
  if (maxTimes < 1 || maxTimes > 20)
    err('fieldKit', undefined, 'maxTimes', 'fieldKit.maxTimes must be 1..20.');
  for (const id of kit.recipeIds ?? []) {
    const r = recipes.find(([x]) => x.id === id)?.[0];
    if (!r) err('fieldKit', undefined, 'recipeIds', `fieldKit lists unknown recipe '${id}'.`);
    else if (!isField(r.where))
      err(
        'fieldKit',
        undefined,
        'recipeIds',
        `fieldKit lists '${id}', which isn't a field recipe.`,
      );
  }

  // ---- the crafting section
  const crafting = b.crafting ?? {};
  for (const s of crafting.craftSecondsByTier ?? [])
    if (s < 0)
      err(
        'crafting',
        undefined,
        'craftSecondsByTier',
        'crafting.craftSecondsByTier has a negative time.',
      );
  const lim = crafting.buildOrders ?? {};
  if (
    (lim.maxRunning ?? 1) < 1 ||
    (lim.maxWaiting ?? 3) < 0 ||
    (lim.maxSteps ?? 40) < 1 ||
    (lim.maxCrafts ?? 200) < 1 ||
    (lim.maxQuantity ?? 20) < 1
  )
    err(
      'crafting',
      undefined,
      'buildOrders',
      'crafting.buildOrders needs maxRunning, maxSteps, maxCrafts and maxQuantity of at least 1 and maxWaiting of at least 0.',
    );
  if ((crafting.workshopNewHours ?? 48) < 1)
    err('crafting', undefined, 'workshopNewHours', 'crafting.workshopNewHours must be at least 1.');
  const power = crafting.power ?? {};
  let anyPower = false;
  for (const m of modules.values())
    for (const l of levelsOf(m))
      if (
        l?.power != null &&
        (n0(l.power.storageWh) > 0 || n0(l.power.solarWhPerHour) > 0 || n0(l.power.outputW) > 0)
      )
        anyPower = true;
  if (anyPower) {
    const fuel = power.fuelItemId === undefined ? 'fuel_can' : power.fuelItemId;
    if (!fuel || !items.has(fuel))
      err(
        'crafting',
        undefined,
        'power.fuelItemId',
        `crafting.power.fuelItemId '${fuel ?? ''}' isn't an item.`,
      );
    if ((power.whPerFuel ?? 2000) < 1)
      err('crafting', undefined, 'power.whPerFuel', 'crafting.power.whPerFuel must be at least 1.');
  }
  for (const t of crafting.room?.toolWall ?? [])
    if (!isCat(t, WORKSHOP_TOOL))
      err(
        'crafting',
        undefined,
        'room.toolWall',
        `The tool wall lists '${t}', which isn't a workshop_tool.`,
      );

  // ---- room slots: every facility a recipe needs has one, and no two share one
  const moduleSlot = (m: Module) =>
    levelsOf(m).find((l) => l != null && l.roomSlot)?.roomSlot ?? '';
  const slotOwner = new Map<string, string>();
  for (const [id, m] of modules) {
    const slot = moduleSlot(m);
    if (!slot) continue;
    const other = slotOwner.get(slot);
    if (other !== undefined)
      moduleErr(id, 'levels', `Facilities '${other}' and '${id}' share room slot '${slot}'.`);
    else slotOwner.set(slot, id);
  }
  for (const [r, n] of recipes)
    for (const q of r.requires ?? []) {
      const fm = q?.facility ? modules.get(q.facility) : undefined;
      if (q != null && fm && !moduleSlot(fm))
        recipeErr(
          n,
          'requires',
          `Recipe '${s0(r.id)}' needs facility '${q.facility}', which has no room slot.`,
        );
    }

  // ---- power: a powered recipe or level hangs off a power source
  function hangsOffPower(moduleId: string, level: number, depth: number): boolean {
    const m = modules.get(moduleId);
    if (depth > 12 || !m) return false;
    const levels = levelsOf(m);
    for (let i = 0; i < Math.min(Math.max(1, level), levels.length); i++) {
      const l = levels[i];
      if (l?.power != null && (n0(l.power.storageWh) > 0 || n0(l.power.outputW) > 0)) return true;
      for (const q of l?.requires ?? [])
        if (q != null && q.facility && hangsOffPower(q.facility, n0(q.level), depth + 1))
          return true;
    }
    return false;
  }
  /** `TechRules.Requires`: the recipe's requires, else its legacy station at level max(1, n). */
  const requiresOf = (r: Recipe): NonNullable<Req>[] =>
    r.requires != null && r.requires.length > 0
      ? r.requires.filter((q): q is NonNullable<Req> => q != null)
      : r.requiredModuleId
        ? [{ facility: r.requiredModuleId, level: Math.max(1, n0(r.requiredModuleLevel)) }]
        : [];
  for (const [r, n] of recipes) {
    if (n0(r.powerWh) <= 0 || isField(r.where)) continue;
    const ok = requiresOf(r).some((q) => q.facility && hangsOffPower(q.facility, n0(q.level), 0));
    if (!ok)
      recipeErr(
        n,
        'powerWh',
        `Recipe '${s0(r.id)}' draws power but needs no facility that hangs off a power source.`,
      );
  }
  for (const [id, m] of modules)
    levelsOf(m).forEach((l, li) => {
      if (l == null || n0(l.powerWhPerDay) <= 0) return;
      const ok = (l.requires ?? []).some(
        (q) => q != null && q.facility && hangsOffPower(q.facility, n0(q.level), 0),
      );
      if (!ok)
        moduleErr(
          id,
          `levels.${li}.powerWhPerDay`,
          `Facility '${id}' level ${n0(l.level)} draws power daily but needs no facility that hangs off a power source.`,
        );
    });

  // ---- key parts: never loot, a plain recipe output, a boss reward or a non-dev trigger grant
  for (const id of lootItems)
    if (isCat(id, KEY_PART))
      err(
        'lootTables',
        undefined,
        '',
        `Key part '${id}' is in a loot table (key parts come only from their missions).`,
      );
  for (const [r, n] of recipes)
    for (const s of r.outputs ?? [])
      if (s != null && isCat(s.itemId, KEY_PART) && !isRebuild(r))
        recipeErr(
          n,
          'outputs',
          `Recipe '${s0(r.id)}' makes key part '${s0(s.itemId)}' without a blueprint requirement.`,
        );
  (b.bosses ?? []).forEach((boss, n) => {
    for (const s of boss?.firstClearRewards ?? [])
      if (s != null && isCat(s.itemId, KEY_PART))
        err(
          'bosses',
          n,
          'firstClearRewards',
          `Boss '${s0(boss!.id)}' rewards key part '${s0(s.itemId)}'.`,
        );
  });
  (b.triggers ?? []).forEach((t, n) => {
    if (t == null || t.devOnly || t.items == null) return;
    for (const s of t.items.stacks ?? [])
      if (s != null && isCat(s.itemId, KEY_PART))
        err(
          'triggers',
          n,
          'items.stacks',
          `Trigger '${s0(t.id)}' grants key part '${s0(s.itemId)}'.`,
        );
  });

  // ---- tiers, gates, milestones, key parts, rebuilds (only for a bundle with the tech tree)
  if (tiers.size === 0) return;
  const levelTier = (moduleId: string, level: number) => {
    const levels = modules.get(moduleId)?.levels;
    if (!levels || levels.length === 0) return 0;
    const l = levels[Math.min(Math.max(1, level), levels.length) - 1];
    return l == null ? 0 : n0(l.unlocksTier) > 0 ? n0(l.unlocksTier) : n0(l.tier);
  };
  const tierErr = (tier: number, path: string, m: string) =>
    err('techTiers', tierIndex.get(tier), path, m);

  const keyPartTier = new Map<string, number>();
  for (let t = 0; t < tiers.size; t++)
    if (!tiers.has(t))
      err(
        'techTiers',
        undefined,
        '',
        `Tech tiers must run 0..${tiers.size - 1} with no gaps (tier ${t} is missing).`,
      );

  const gateOf = new Map<number, string>();
  for (const [id, m] of modules) {
    const l1 = levelsOf(m)[0] ?? null;
    if (l1 == null || n0(l1.unlocksTier) <= 0) continue;
    const other = gateOf.get(n0(l1.unlocksTier));
    if (other !== undefined)
      moduleErr(
        id,
        'levels.0.unlocksTier',
        `Facilities '${other}' and '${id}' both unlock tier ${n0(l1.unlocksTier)}.`,
      );
    else gateOf.set(n0(l1.unlocksTier), id);
  }

  function milestoneProblem(ms: string, tier: number): string | undefined {
    if (!ms) return 'is empty.';
    const at = ms.indexOf('@');
    if (at >= 0) {
      const id = ms.substring(0, at);
      const level = tryInt(ms.substring(at + 1));
      const m = modules.get(id);
      if (level === undefined || !m || level < 1 || level > levelsOf(m).length)
        return "isn't a facility level.";
      return levelTier(id, level) === tier ? undefined : 'belongs to another tier.';
    }
    if (modules.has(ms)) return levelTier(ms, 1) === tier ? undefined : 'belongs to another tier.';
    if (!items.has(ms)) return "isn't a facility, a facility@level or an item.";
    for (const [r] of recipes) {
      if (isField(r.where)) continue;
      for (const o of r.outputs ?? [])
        if (o != null && o.itemId === ms && n0(r.tier) === tier) return undefined;
    }
    return 'has no home recipe in this tier.';
  }

  for (const t of tiers.values()) {
    const tier = n0(t.tier);
    const owner = `Tech tier ${tier}`;
    if (tier === 0) {
      if (t.gateModuleId)
        tierErr(tier, 'gateModuleId', `${owner} has a gate (every run starts in tier 0).`);
    } else {
      const gate = t.gateModuleId ? modules.get(t.gateModuleId) : undefined;
      if (!gate)
        tierErr(
          tier,
          'gateModuleId',
          `${owner} needs a gate facility that exists ('${s0(t.gateModuleId)}').`,
        );
      else {
        const l1 = levelsOf(gate)[0] ?? null;
        if (l1 == null || n0(l1.unlocksTier) !== tier)
          tierErr(
            tier,
            'gateModuleId',
            `${owner}'s gate '${s0(gate.id)}' must unlock tier ${tier} on its first level.`,
          );
        for (const q of l1?.requires ?? [])
          if (q != null && q.facility && levelTier(q.facility, n0(q.level)) > tier - 1)
            tierErr(
              tier,
              'gateModuleId',
              `${owner}'s gate needs '${q.facility}', which is above tier ${tier - 1} (gates unlock tiers in order).`,
            );
      }
    }

    if (t.milestones == null || t.milestones.length === 0)
      tierErr(tier, 'milestones', `${owner} has no mastery milestones.`);
    for (const ms of t.milestones ?? []) {
      const problem = milestoneProblem(ms ?? '', tier);
      if (problem !== undefined)
        tierErr(tier, 'milestones', `${owner} milestone '${ms ?? ''}' ${problem}`);
    }

    for (const part of t.keyParts ?? []) {
      const other = keyPartTier.get(part);
      if (other !== undefined)
        tierErr(tier, 'keyParts', `Key part '${part}' is in tiers ${other} and ${tier}.`);
      else keyPartTier.set(part, tier);
      const kp = keyPartDefs.get(part);
      if (kp) {
        if (n0(kp.tier) !== tier)
          tierErr(
            tier,
            'keyParts',
            `Key part '${part}' says tier ${n0(kp.tier)} but tier ${tier} lists it.`,
          );
      } else if (!isCat(part, KEY_PART))
        tierErr(
          tier,
          'keyParts',
          `${owner} lists key part '${part}', which is neither a keyParts[] entry nor a key_part item.`,
        );
    }

    if (t.keyParts != null && t.keyParts.length > 0 && tier < 2)
      tierErr(tier, 'keyParts', `${owner} has key parts (tier 1's Workbench never has any).`);
    if (t.questMissionId && !(b.missions ?? []).some((x) => x?.id === t.questMissionId))
      tierErr(
        tier,
        'questMissionId',
        `${owner} names unknown quest mission '${t.questMissionId}'.`,
      );

    for (const rid of t.rebuild ?? []) {
      const r = recipes.find(([x]) => x.id === rid)?.[0];
      if (!r) {
        tierErr(tier, 'rebuild', `${owner} lists unknown rebuild recipe '${rid}'.`);
        continue;
      }
      if (!(r.requires ?? []).some((q) => q != null && n0(q.blueprint) === tier))
        tierErr(tier, 'rebuild', `Rebuild recipe '${rid}' must need the tier ${tier} blueprint.`);
    }
  }

  for (const [unlocked, id] of gateOf)
    if (!tiers.has(unlocked))
      moduleErr(
        id,
        'levels.0.unlocksTier',
        `Facility '${id}' unlocks tier ${unlocked}, which has no techTiers entry.`,
      );

  // Tier order: a recipe's tier covers each facility and tool it needs and the lowest tier of each input.
  const found = new Set(lootItems);
  for (const s of b.survival?.starterBase ?? []) found.add(s?.itemId ?? '');
  for (const s of b.survival?.starterCarried ?? []) found.add(s?.itemId ?? '');
  for (const i of items.values())
    if (i.category === RAW || i.category === MATERIAL) found.add(i.id ?? '');

  const lowest = new Map<string, number>();
  for (const [r] of recipes) {
    if (isField(r.where)) continue;
    for (const o of r.outputs ?? []) {
      if (o == null) continue;
      const id = o.itemId ?? '';
      const t = lowest.get(id);
      lowest.set(id, t === undefined ? n0(r.tier) : Math.min(t, n0(r.tier)));
    }
  }
  for (const id of found) lowest.set(id, 0);

  for (const [r, n] of recipes) {
    if (isField(r.where)) continue;
    const rt = n0(r.tier);
    for (const q of r.requires ?? []) {
      if (q == null) continue;
      if (q.facility && modules.has(q.facility)) {
        const ft = levelTier(q.facility, n0(q.level));
        if (ft > rt)
          recipeErr(
            n,
            'tier',
            `Recipe '${s0(r.id)}' is tier ${rt} but needs '${q.facility}' from tier ${ft}.`,
          );
      } else if (q.tool && items.has(q.tool) && n0(items.get(q.tool)!.tier) > rt)
        recipeErr(
          n,
          'tier',
          `Recipe '${s0(r.id)}' is tier ${rt} but needs tool '${q.tool}' from tier ${n0(items.get(q.tool)!.tier)}.`,
        );
    }
    for (const s of r.inputs ?? []) {
      const lt = s != null ? lowest.get(s.itemId ?? '') : undefined;
      if (s != null && lt !== undefined && lt > rt && !isCat(s.itemId, KEY_PART))
        recipeErr(
          n,
          'inputs',
          `Recipe '${s0(r.id)}' is tier ${rt} but uses '${s0(s.itemId)}', first made in tier ${lt}.`,
        );
    }
  }

  for (const [id, m] of modules)
    levelsOf(m).forEach((l, li) => {
      if (l == null) return;
      const lt = n0(l.unlocksTier) > 0 ? n0(l.unlocksTier) : n0(l.tier);
      for (const q of l.requires ?? [])
        if (
          q != null &&
          q.facility &&
          modules.has(q.facility) &&
          levelTier(q.facility, n0(q.level)) > lt
        )
          moduleErr(
            id,
            `levels.${li}.requires`,
            `Facility '${id}' level ${n0(l.level)} is tier ${lt} but needs '${q.facility}' from a higher tier.`,
          );
    });

  // Reachable: everything in the tree can be made from loot (and key parts from their missions).
  const reach = new Set(found);
  for (const i of items.values()) if (i.category === KEY_PART) reach.add(i.id ?? '');
  let grew = true;
  while (grew) {
    grew = false;
    for (const [r] of recipes) {
      const ok =
        (r.inputs ?? []).every((s) => s == null || reach.has(s.itemId ?? '')) &&
        (r.requires ?? []).every((q) => q == null || !q.tool || reach.has(q.tool));
      if (!ok) continue;
      for (const o of r.outputs ?? [])
        if (o != null && !reach.has(o.itemId ?? '')) {
          reach.add(o.itemId ?? '');
          grew = true;
        }
    }
  }
  const tree = new Set<string>();
  for (const [r] of recipes)
    for (const s of [...(r.inputs ?? []), ...(r.outputs ?? [])])
      if (s != null && items.has(s.itemId ?? '')) tree.add(s.itemId ?? '');
  for (const i of items.values())
    if (i.category === INTERMEDIATE || i.category === PART || i.category === WORKSHOP_TOOL)
      tree.add(i.id ?? '');
  const ordinal = (a: string, c: string) => (a < c ? -1 : a > c ? 1 : 0);
  for (const id of [...tree].sort(ordinal))
    if (!reach.has(id))
      err(
        'items',
        undefined,
        '',
        `Item '${id}' can't be reached from loot (no recipe chain starts from found items; check for a cycle with no entry).`,
      );
}
